import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  LogOut, Users, Calendar, Package, Lock, Eye, EyeOff, ArrowLeft, 
  Plus, Trash2, ShoppingCart, Receipt, History, Ticket, X, Search,
  CreditCard, Banknote, Check, AlertCircle, Settings, Monitor, Image, Clock
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_equestrian-hub-20/artifacts/0lyoa6pm_No%20hatter%2C%20feher.png";

const ADMIN_PASSWORD = "gepard2021";

// Cash denominations in HUF
const DENOMINATIONS = [20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5];

// Hungarian cash rounding (to nearest 5 Ft)
const roundCash = (amount) => Math.round(amount / 5) * 5;

export default function GepardKassza() {
  const [view, setView] = useState("login");
  const [pin, setPin] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [currentCashier, setCurrentCashier] = useState(null);
  const [cashierToken, setCashierToken] = useState("");
  const [activeAdminTab, setActiveAdminTab] = useState("kasszasok");
  
  // Data states
  const [cashiers, setCashiers] = useState([]);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  
  // Event details/statistics state
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventStats, setEventStats] = useState(null);
  const [eventTransactions, setEventTransactions] = useState([]);
  
  // Cashier event info
  const [cashierEvent, setCashierEvent] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [eventStarted, setEventStarted] = useState(false);
  
  // Modal states
  const [showCashierModal, setShowCashierModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  
  // Kassza Display (Kosár) settings
  const [displaySettings, setDisplaySettings] = useState({ enabled: false, ads: [] });
  const [showAdModal, setShowAdModal] = useState(false);
  const [adForm, setAdForm] = useState({ url: '', duration: 5 });
  
  // Form states
  const [cashierForm, setCashierForm] = useState({ name: "", birth_date: "", phone: "", email: "" });
  const [eventForm, setEventForm] = useState({ name: "", date: "", start_time: "", location: "", product_ids: [], cashier_ids: [] });
  const [productForm, setProductForm] = useState({ name: "", price: 0, sku: "" });
  const [voucherForm, setVoucherForm] = useState({ is_winning: false, prize_name: "", issue_date: new Date().toISOString().split('T')[0] });
  
  // POS states
  const [cart, setCart] = useState([]);
  const [posMode, setPosMode] = useState("products"); // products or amount
  const [customAmount, setCustomAmount] = useState("");
  const [customAmountDisplay, setCustomAmountDisplay] = useState(""); // For terminal-style display
  const [paymentStep, setPaymentStep] = useState("cart"); // cart, payment, done
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paidDenominations, setPaidDenominations] = useState([]);
  
  // Voucher redemption
  const [voucherCode, setVoucherCode] = useState("");
  const [foundVoucher, setFoundVoucher] = useState(null);
  const [redeemForm, setRedeemForm] = useState({ name: "", phone: "", email: "", birth_date: "" });

  // Fetch data functions
  const fetchCashiers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/kassza/cashiers`, { params: { admin_password: ADMIN_PASSWORD } });
      setCashiers(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/kassza/events`);
      setEvents(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/kassza/products`);
      setProducts(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!cashierToken) return;
    try {
      const res = await axios.get(`${API}/kassza/orders`, { params: { token: cashierToken } });
      setOrders(res.data);
    } catch (err) { console.error(err); }
  }, [cashierToken]);

  const fetchVouchers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/kassza/vouchers`, { params: { admin_password: ADMIN_PASSWORD } });
      setVouchers(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchDisplaySettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/kassza/display/settings`);
      setDisplaySettings(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const updateDisplaySettings = async (newSettings) => {
    try {
      await axios.put(`${API}/kassza/display/settings`, newSettings, { params: { admin_password: ADMIN_PASSWORD } });
      setDisplaySettings(newSettings);
      toast.success("Beállítások mentve!");
    } catch (err) {
      toast.error("Hiba a mentés során");
    }
  };

  const fetchEventStats = useCallback(async (eventId) => {
    try {
      const res = await axios.get(`${API}/kassza/events/${eventId}/stats`, { params: { admin_password: ADMIN_PASSWORD } });
      setEventStats(res.data);
    } catch (err) { 
      console.error(err);
      toast.error("Nem sikerült betölteni a statisztikákat");
    }
  }, []);

  useEffect(() => {
    if (view === "admin") {
      fetchCashiers();
      fetchEvents();
      fetchProducts();
      fetchVouchers();
      fetchDisplaySettings();
    }
  }, [view, fetchCashiers, fetchEvents, fetchProducts, fetchVouchers]);

  useEffect(() => {
    if (view === "admin" && selectedEvent) {
      fetchEventStats(selectedEvent.id);
      fetchEventTransactions(selectedEvent.id);
    }
  }, [view, selectedEvent, fetchEventStats]);

  const fetchEventTransactions = useCallback(async (eventId) => {
    try {
      const res = await axios.get(`${API}/kassza/events/${eventId}/transactions`, { params: { admin_password: ADMIN_PASSWORD } });
      setEventTransactions(res.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    if (view === "cashier" || view === "pos" || view === "orders" || view === "voucher-redeem") {
      fetchProducts();
      fetchOrders();
      fetchEvents();  // Need events for event-bound product filtering
    }
  }, [view, fetchProducts, fetchOrders, fetchEvents]);

  // Load cashier's event info - now uses assigned_event from login or finds from cashier_ids
  useEffect(() => {
    // If cashierEvent is already set from login, don't override
    if (cashierEvent) return;
    
    if (currentCashier && events.length > 0) {
      // Old way: check if cashier has event_id (backward compatibility)
      if (currentCashier.event_id) {
        const event = events.find(e => e.id === currentCashier.event_id);
        if (event) {
          setCashierEvent(event);
          return;
        }
      }
      
      // New way: find event where cashier is in cashier_ids for today
      const today = new Date().toISOString().split('T')[0];
      const todayEvent = events.find(e => 
        e.date === today && e.cashier_ids?.includes(currentCashier.id)
      );
      if (todayEvent) {
        setCashierEvent(todayEvent);
      }
    }
  }, [currentCashier, events, cashierEvent]);

  // Countdown timer for event start
  useEffect(() => {
    if (!cashierEvent) {
      setCountdown(null);
      setEventStarted(true); // No event = always available
      return;
    }
    
    const updateCountdown = () => {
      const eventDateTime = new Date(`${cashierEvent.date}T${cashierEvent.start_time || '00:00'}`);
      const now = new Date();
      const diff = eventDateTime - now;
      
      if (diff <= 0) {
        setEventStarted(true);
        setCountdown(null);
      } else {
        setEventStarted(false);
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        if (days > 0) {
          setCountdown(`${days} nap ${hours} óra ${minutes} perc`);
        } else if (hours > 0) {
          setCountdown(`${hours} óra ${minutes} perc ${seconds} mp`);
        } else {
          setCountdown(`${minutes} perc ${seconds} mp`);
        }
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [cashierEvent]);

  // PIN handlers
  const handlePinDigit = async (digit) => {
    if (pin.length < 4) {
      const newPin = pin + digit;
      setPin(newPin);
      setError("");
      
      if (newPin.length === 4) {
        try {
          const res = await axios.post(`${API}/kassza/login`, { pin: newPin });
          setCurrentCashier(res.data.cashier);
          setCashierToken(res.data.token);
          // Set the assigned event from login response
          if (res.data.assigned_event) {
            setCashierEvent(res.data.assigned_event);
          }
          setView("cashier");
          setPin("");
        } catch (err) {
          setError(err.response?.data?.detail || "Hibás PIN kód!");
          setPin("");
        }
      }
    }
  };

  const handlePinBackspace = () => { setPin(prev => prev.slice(0, -1)); setError(""); };
  const handlePinClear = () => { setPin(""); setError(""); };

  // Admin login
  const handleAdminLogin = (e) => {
    e.preventDefault();
    if (adminPassword === ADMIN_PASSWORD) {
      setView("admin");
      setAdminPassword("");
      setError("");
    } else {
      setError("Hibás jelszó!");
    }
  };

  const handleLogout = () => {
    setCurrentCashier(null);
    setCashierToken("");
    setCashierEvent(null);
    setView("login");
    setPin("");
    setAdminPassword("");
    setError("");
    setCart([]);
    setPaymentStep("cart");
  };

  // CRUD handlers
  const handleCreateCashier = async () => {
    try {
      const res = await axios.post(`${API}/kassza/cashiers`, cashierForm, { params: { admin_password: ADMIN_PASSWORD } });
      toast.success(`Kasszás létrehozva! PIN: ${res.data.pin}`);
      setShowCashierModal(false);
      setCashierForm({ name: "", birth_date: "", phone: "", email: "", event_id: "" });
      fetchCashiers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba");
    }
  };

  const handleDeleteCashier = async (id) => {
    if (!window.confirm("Biztosan törlöd?")) return;
    try {
      await axios.delete(`${API}/kassza/cashiers/${id}`, { params: { admin_password: ADMIN_PASSWORD } });
      toast.success("Kasszás törölve");
      fetchCashiers();
    } catch (err) {
      toast.error("Hiba");
    }
  };

  const handleCreateEvent = async () => {
    try {
      await axios.post(`${API}/kassza/events`, eventForm, { params: { admin_password: ADMIN_PASSWORD } });
      toast.success("Rendezvény létrehozva!");
      setShowEventModal(false);
      setEventForm({ name: "", date: "", start_time: "", location: "", product_ids: [] });
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba");
    }
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;
    try {
      await axios.put(`${API}/kassza/events/${editingEvent.id}`, eventForm, { params: { admin_password: ADMIN_PASSWORD } });
      toast.success("Rendezvény módosítva!");
      setEditingEvent(null);
      setEventForm({ name: "", date: "", start_time: "", location: "", product_ids: [] });
      fetchEvents();
      // Update selected event too
      if (selectedEvent && selectedEvent.id === editingEvent.id) {
        setSelectedEvent({...editingEvent, ...eventForm});
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba");
    }
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm("Biztosan törlöd?")) return;
    try {
      await axios.delete(`${API}/kassza/events/${id}`, { params: { admin_password: ADMIN_PASSWORD } });
      toast.success("Rendezvény törölve");
      fetchEvents();
    } catch (err) {
      toast.error("Hiba");
    }
  };

  const handleDeleteTransaction = async (orderId) => {
    if (!window.confirm("Biztosan törlöd ezt a tranzakciót? Ez véglegesen eltávolítja minden statisztikából!")) return;
    try {
      await axios.delete(`${API}/kassza/orders/${orderId}`, { params: { admin_password: ADMIN_PASSWORD } });
      toast.success("Tranzakció törölve");
      // Refresh stats and transactions
      if (selectedEvent) {
        fetchEventStats(selectedEvent.id);
        fetchEventTransactions(selectedEvent.id);
      }
    } catch (err) {
      toast.error("Hiba a törlés során");
    }
  };

  const handleCreateProduct = async () => {
    try {
      await axios.post(`${API}/kassza/products`, productForm, { params: { admin_password: ADMIN_PASSWORD } });
      toast.success("Termék létrehozva!");
      setShowProductModal(false);
      setProductForm({ name: "", price: 0, sku: "" });
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba");
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Biztosan törlöd?")) return;
    try {
      await axios.delete(`${API}/kassza/products/${id}`, { params: { admin_password: ADMIN_PASSWORD } });
      toast.success("Termék törölve");
      fetchProducts();
    } catch (err) {
      toast.error("Hiba");
    }
  };

  const handleCreateVoucher = async () => {
    try {
      const res = await axios.post(`${API}/kassza/vouchers`, voucherForm, { params: { admin_password: ADMIN_PASSWORD } });
      // Format code with spaces: ABCD1234EF -> ABCD 1234 EF
      const code = res.data.voucher.code;
      const formattedCode = `${code.slice(0,4)} ${code.slice(4,8)} ${code.slice(8)}`;
      toast.success(`Szelvény létrehozva! Kód: ${formattedCode}`);
      setShowVoucherModal(false);
      setVoucherForm({ is_winning: false, prize_name: "", issue_date: new Date().toISOString().split('T')[0] });
      fetchVouchers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba");
    }
  };

  // POS functions
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => item.id === product.id ? { ...item, qty: item.qty + 1 } : item);
      }
      return [...prev, { ...product, qty: 1 }];
    });
  };

  // Terminal-style numpad for custom amount
  const handleAmountDigit = (digit) => {
    if (customAmountDisplay.length < 7) { // Max 9999999 Ft
      setCustomAmountDisplay(prev => prev + digit);
    }
  };
  
  const handleAmountClear = () => {
    setCustomAmountDisplay("");
  };
  
  const handleAmountBackspace = () => {
    setCustomAmountDisplay(prev => prev.slice(0, -1));
  };
  
  const addCustomAmountToCart = () => {
    const amount = parseInt(customAmountDisplay);
    if (amount > 0) {
      const customId = `custom_${Date.now()}`;
      setCart(prev => [...prev, { id: customId, name: "Egyéb összeg", price: amount, qty: 1 }]);
      setCustomAmountDisplay("");
    }
  };

  const addCustomAmount = () => {
    const amount = parseInt(customAmount);
    if (amount > 0) {
      const customId = `custom_${Date.now()}`;
      setCart(prev => [...prev, { id: customId, name: "Egyéb összeg", price: amount, qty: 1 }]);
      setCustomAmount("");
    }
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const updateQty = (id, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = item.qty + delta;
        // If newQty <= 0, mark item for removal by returning null
        if (newQty <= 0) return null;
        return { ...item, qty: newQty };
      }
      return item;
    }).filter(item => item !== null));
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
  const roundedTotal = paymentMethod === "cash" ? roundCash(cartTotal) : cartTotal;

  // Send cart updates to display
  useEffect(() => {
    if (!cashierToken || view !== "terminal") return;
    
    const updateDisplayCart = async () => {
      try {
        await axios.put(`${API}/kassza/display/cart`, {
          items: cart.map(item => ({ name: item.name, price: item.price, qty: item.qty })),
          total: cartTotal
        }, { params: { token: cashierToken } });
      } catch (err) {
        console.error("Failed to update display cart", err);
      }
    };
    
    updateDisplayCart();
  }, [cart, cartTotal, cashierToken, view]);
  const paidTotal = paidDenominations.reduce((sum, d) => sum + d, 0);
  const remaining = roundedTotal - paidTotal;
  const change = paidTotal > roundedTotal ? paidTotal - roundedTotal : 0;

  const addDenomination = (amount) => {
    setPaidDenominations(prev => [...prev, amount]);
  };

  const removeDenomination = (index) => {
    setPaidDenominations(prev => prev.filter((_, i) => i !== index));
  };

  const finalizeOrder = async () => {
    try {
      await axios.post(`${API}/kassza/orders`, {
        items: cart.map(item => ({ name: item.name, price: item.price, qty: item.qty })),
        total: roundedTotal,
        payment_method: paymentMethod,
        paid_amount: paidTotal,
        change: change,
        event_id: cashierEvent?.id || null
      }, { params: { token: cashierToken } });
      
      toast.success("Rendelés rögzítve!");
      setCart([]);
      setPaidDenominations([]);
      setPaymentStep("cart");
      setPaymentMethod("cash");  // Reset payment method for next order
      setPosMode("products");    // Reset POS mode for next order
      setView("cashier");
    } catch (err) {
      toast.error("Hiba a mentésnél");
    }
  };

  // Voucher search
  const searchVoucher = async () => {
    try {
      const res = await axios.get(`${API}/kassza/vouchers/search/${voucherCode}`, { params: { token: cashierToken } });
      setFoundVoucher(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Szelvény nem található");
      setFoundVoucher(null);
    }
  };

  const redeemVoucher = async () => {
    try {
      await axios.post(`${API}/kassza/vouchers/redeem/${foundVoucher.code}`, redeemForm, { params: { token: cashierToken } });
      toast.success("Szelvény beváltva!");
      setFoundVoucher(null);
      setVoucherCode("");
      setRedeemForm({ name: "", phone: "", email: "", birth_date: "" });
      setView("cashier");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba");
    }
  };

  // Header component
  const Header = ({ showLogout = false, title = null }) => (
    <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 shadow-xl">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="Gepárd" className="w-12 h-12 object-contain filter brightness-0 invert" />
          <div>
            <h1 className="text-white font-bold text-xl tracking-tight">Gepárd Lovas Egyesület</h1>
            {title && <p className="text-slate-400 text-sm">{title}</p>}
          </div>
        </div>
        {showLogout && (
          <button onClick={handleLogout} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-xl transition-all duration-200 backdrop-blur-sm border border-white/10">
            <LogOut size={18} />
            <span className="font-medium">Kilépés</span>
          </button>
        )}
      </div>
    </header>
  );

  // PIN Pad component - COMPACT VERSION
  const PinPad = () => (
    <div className="space-y-4">
      <div className="flex justify-center gap-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`w-12 h-14 rounded-xl flex items-center justify-center text-2xl font-bold transition-all duration-200 ${
            pin.length > i ? "bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-md shadow-emerald-500/30" : "bg-slate-100 text-slate-300 border-2 border-dashed border-slate-300"
          }`}>
            {pin.length > i ? "●" : ""}
          </div>
        ))}
      </div>
      {error && <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg text-center text-sm font-medium">{error}</div>}
      <div className="grid grid-cols-3 gap-2 max-w-[200px] mx-auto">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
          <button key={num} onClick={() => handlePinDigit(String(num))} className="w-14 h-14 bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-800 text-xl font-bold rounded-xl transition-all duration-150 shadow-sm hover:shadow-md active:scale-95 border border-slate-200">
            {num}
          </button>
        ))}
        <button onClick={handlePinClear} className="w-14 h-14 bg-gradient-to-br from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white text-sm font-bold rounded-xl transition-all duration-150 shadow-sm hover:shadow-md active:scale-95">C</button>
        <button onClick={() => handlePinDigit("0")} className="w-14 h-14 bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-800 text-xl font-bold rounded-xl transition-all duration-150 shadow-sm hover:shadow-md active:scale-95 border border-slate-200">0</button>
        <button onClick={handlePinBackspace} className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-sm font-bold rounded-xl transition-all duration-150 shadow-sm hover:shadow-md active:scale-95">←</button>
      </div>
    </div>
  );

  // ==================== LOGIN VIEW ====================
  if (view === "login") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full text-center border border-slate-200/50">
            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md shadow-emerald-500/30">
              <Lock size={28} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-1">Kassza Terminál</h2>
            <p className="text-slate-500 text-sm mb-6">Jelentkezz be a kasszába a PIN kódoddal</p>
            <button data-testid="kassza-login-btn" onClick={() => setView("pin-entry")} className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-md shadow-emerald-500/30 hover:shadow-lg active:scale-[0.98]">
              Belépés a kasszába
            </button>
          </div>
          <button data-testid="admin-login-btn" onClick={() => setView("admin-login")} className="mt-6 text-slate-400 hover:text-slate-600 text-xs transition-colors flex items-center gap-1">
            <Lock size={12} />Admin belépés
          </button>
        </main>
      </div>
    );
  }

  // ==================== PIN ENTRY VIEW ====================
  if (view === "pin-entry") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full border border-slate-200/50">
            <button data-testid="pin-back-btn" onClick={() => { setView("login"); setPin(""); setError(""); }} className="flex items-center gap-1 text-slate-500 hover:text-slate-700 mb-4 text-sm transition-colors">
              <ArrowLeft size={16} /><span>Vissza</span>
            </button>
            <h2 className="text-lg font-bold text-slate-800 mb-1 text-center">PIN kód megadása</h2>
            <p className="text-slate-500 text-sm mb-5 text-center">Add meg a 4 számjegyű PIN kódodat</p>
            <PinPad />
          </div>
        </main>
      </div>
    );
  }

  // ==================== ADMIN LOGIN VIEW ====================
  if (view === "admin-login") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full border border-slate-200/50">
            <button data-testid="admin-back-btn" onClick={() => { setView("login"); setAdminPassword(""); setError(""); }} className="flex items-center gap-1 text-slate-500 hover:text-slate-700 mb-4 text-sm transition-colors">
              <ArrowLeft size={16} /><span>Vissza</span>
            </button>
            <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md shadow-violet-500/30">
              <Lock size={22} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-slate-800 mb-1 text-center">Admin Belépés</h2>
            <p className="text-slate-500 text-sm mb-5 text-center">Add meg az admin jelszót</p>
            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div className="relative">
                <input data-testid="admin-password-input" type={showPassword ? "text" : "password"} value={adminPassword} onChange={(e) => { setAdminPassword(e.target.value); setError(""); }} placeholder="Jelszó" className="w-full border-2 border-slate-200 focus:border-violet-500 rounded-lg px-3 py-3 pr-10 text-slate-800 outline-none transition-colors" autoFocus />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {error && <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg text-center text-sm font-medium">{error}</div>}
              <button data-testid="admin-login-submit" type="submit" className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-md shadow-violet-500/30 hover:shadow-lg active:scale-[0.98]">
                Belépés
              </button>
            </form>
          </div>
        </main>
      </div>
    );
  }

  // ==================== CASHIER MAIN VIEW ====================
  if (view === "cashier") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <Header showLogout title={`Kasszás: ${currentCashier?.name}`} />
        <main className="max-w-4xl mx-auto px-6 py-6">
          
          {/* Event Info Card */}
          {cashierEvent && (
            <div data-testid="cashier-event-info" className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-5 text-white mb-6 shadow-lg">
              <h3 className="text-lg font-bold mb-3">{cashierEvent.name}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><span className="opacity-75">Dátum:</span><br /><span className="font-semibold">{cashierEvent.date}</span></div>
                <div><span className="opacity-75">Kezdés:</span><br /><span className="font-semibold">{cashierEvent.start_time || '-'}</span></div>
                <div><span className="opacity-75">Helyszín:</span><br /><span className="font-semibold">{cashierEvent.location || '-'}</span></div>
                <div><span className="opacity-75">Termékek:</span><br /><span className="font-semibold">{cashierEvent.product_ids?.length || 0} db</span></div>
              </div>
            </div>
          )}

          {/* Countdown / Work Start Info */}
          {cashierEvent && !eventStarted && (
            <div data-testid="event-countdown-box" className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 mb-6 text-center">
              <p className="text-amber-800 font-medium mb-2">Munkaidő kezdése:</p>
              <p data-testid="event-countdown" className="text-3xl font-bold text-amber-600">{countdown}</p>
              <p className="text-amber-700 text-sm mt-2">A rendezvény kezdéséig a kassza funkciók nem elérhetők</p>
            </div>
          )}

          {eventStarted && cashierEvent && (
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-4 mb-6 text-center">
              <p className="text-emerald-700 font-semibold flex items-center justify-center gap-2">
                <Check size={20} />A rendezvény elkezdődött - a kassza funkciók elérhetők
              </p>
            </div>
          )}

          {/* Main Action Buttons */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: ShoppingCart, testid: "action-order", label: "Rendelés rögzítése", color: "from-emerald-500 to-teal-600", shadow: "shadow-emerald-500/30", onClick: () => setView("pos"), disabled: !eventStarted },
              { icon: Receipt, testid: "action-invoice", label: "Számla rögzítése", color: "from-blue-500 to-cyan-600", shadow: "shadow-blue-500/30", onClick: () => toast.info("Hamarosan elérhető"), disabled: !eventStarted },
              { icon: History, testid: "action-history", label: "Rendelések", color: "from-amber-500 to-orange-600", shadow: "shadow-amber-500/30", onClick: () => setView("orders"), disabled: false },
              { icon: Ticket, testid: "action-voucher", label: "Szelvény beváltása", color: "from-rose-500 to-pink-600", shadow: "shadow-rose-500/30", onClick: () => setView("voucher-redeem"), disabled: !eventStarted },
            ].map((item, idx) => (
              <button 
                key={idx} 
                data-testid={item.testid}
                onClick={item.disabled ? undefined : item.onClick} 
                disabled={item.disabled}
                className={`bg-white rounded-2xl shadow-lg p-6 border border-slate-200/50 transition-all duration-200 ${
                  item.disabled 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
                }`}
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mb-3 shadow-lg ${item.shadow} ${item.disabled ? 'grayscale' : ''}`}>
                  <item.icon size={28} className="text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-800">{item.label}</h3>
              </button>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // ==================== POS VIEW ====================
  if (view === "pos") {
    // Filter products based on current event (from cashierEvent, not cashier.event_id)
    // If event has no products assigned, show all products
    const eventProducts = cashierEvent && cashierEvent.product_ids?.length > 0
      ? products.filter(p => cashierEvent.product_ids.includes(p.id))
      : products;

    if (paymentStep === "payment") {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
          <Header showLogout title="Fizetés" />
          <main className="max-w-4xl mx-auto px-6 py-6">
            <button onClick={() => { setPaymentStep("cart"); setPaidDenominations([]); }} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4">
              <ArrowLeft size={18} /><span>Vissza</span>
            </button>
            
            <div className="bg-white rounded-2xl shadow-xl p-4 md:p-6 border border-slate-200/50 max-w-lg mx-auto">
              {/* Payment method selection - smaller */}
              <div className="flex gap-3 mb-4">
                <button data-testid="payment-cash-btn" onClick={() => { setPaymentMethod("cash"); setPaidDenominations([]); }} className={`flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all text-sm ${paymentMethod === "cash" ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md" : "bg-slate-100 text-slate-600"}`}>
                  <Banknote size={20} />Készpénz
                </button>
                <button data-testid="payment-card-btn" onClick={() => { setPaymentMethod("card"); setPaidDenominations([]); }} className={`flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all text-sm ${paymentMethod === "card" ? "bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-md" : "bg-slate-100 text-slate-600"}`}>
                  <CreditCard size={20} />Kártya
                </button>
              </div>

              {/* Amount display - smaller */}
              <div className="bg-slate-50 rounded-xl p-4 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 text-sm">Végösszeg:</span>
                  <span className="text-xl font-bold text-slate-800">{roundedTotal.toLocaleString('hu-HU')} Ft</span>
                </div>
                {paymentMethod === "cash" && cartTotal !== roundedTotal && (
                  <p className="text-xs text-slate-500 mt-1">Kerekítve {cartTotal.toLocaleString('hu-HU')} Ft-ról</p>
                )}
              </div>

              {/* Cash payment */}
              {paymentMethod === "cash" && (
                <>
                  {/* Paid denominations */}
                  {paidDenominations.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-slate-600 mb-2">Befizetett címletek:</p>
                      <div className="flex flex-wrap gap-1">
                        {paidDenominations.map((d, i) => (
                          <button key={i} onClick={() => removeDenomination(i)} className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-xs font-medium flex items-center gap-1 hover:bg-emerald-200">
                            {d.toLocaleString('hu-HU')} <X size={12} />
                          </button>
                        ))}
                      </div>
                      <p className="mt-2 text-slate-600 text-sm">Befizetett: <span className="font-bold">{paidTotal.toLocaleString('hu-HU')} Ft</span></p>
                    </div>
                  )}

                  {/* Remaining or change */}
                  {remaining > 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                      <p className="text-amber-800 font-semibold text-sm">Még fizetendő: {remaining.toLocaleString('hu-HU')} Ft</p>
                    </div>
                  ) : change > 0 ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4">
                      <p className="text-emerald-800 font-semibold text-sm">Visszajáró: {change.toLocaleString('hu-HU')} Ft</p>
                    </div>
                  ) : null}

                  {/* Denomination buttons - uniform small size */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {DENOMINATIONS.map(d => (
                      <button key={d} data-testid={`denom-${d}`} onClick={() => addDenomination(d)} className="bg-white border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 text-slate-700 py-2 px-1 rounded-lg text-sm font-semibold transition-all">
                        {d.toLocaleString('hu-HU')}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Card payment */}
              {paymentMethod === "card" && (
                <div data-testid="card-payment-info" className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 text-center">
                  <CreditCard size={36} className="text-blue-500 mx-auto mb-2" />
                  <p className="text-blue-800 font-semibold">Kártyás fizetés: {roundedTotal.toLocaleString('hu-HU')} Ft</p>
                  <p className="text-blue-600 text-xs mt-1">Használd a terminált a fizetéshez</p>
                </div>
              )}

              {/* Finalize button */}
              {(paymentMethod === "card" || remaining <= 0) && (
                <button data-testid="finalize-order-btn" onClick={finalizeOrder} className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold py-3 rounded-xl transition-all shadow-lg">
                  Rendelés rögzítése
                </button>
              )}
            </div>
          </main>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <Header showLogout title="Rendelés rögzítése" />
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6">
          <button onClick={() => { setView("cashier"); setCart([]); }} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4">
            <ArrowLeft size={18} /><span>Vissza</span>
          </button>
          
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            {/* Left side - Products */}
            <div className="flex-1 order-2 lg:order-1">
              {/* Mode selector */}
              <div className="flex gap-2 mb-4">
                <button data-testid="mode-products" onClick={() => setPosMode("products")} className={`px-4 md:px-6 py-2 md:py-3 rounded-xl font-semibold transition-all text-sm md:text-base ${posMode === "products" ? "bg-emerald-500 text-white" : "bg-white text-slate-600"}`}>
                  Termék választása
                </button>
                <button data-testid="mode-amount" onClick={() => setPosMode("amount")} className={`px-4 md:px-6 py-2 md:py-3 rounded-xl font-semibold transition-all text-sm md:text-base ${posMode === "amount" ? "bg-emerald-500 text-white" : "bg-white text-slate-600"}`}>
                  Összeg beírása
                </button>
              </div>

              {posMode === "products" ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {(eventProducts.length > 0 ? eventProducts : products).map((product, idx) => (
                    <button key={product.id} data-testid={`product-card-${idx}`} onClick={() => addToCart(product)} className="bg-white rounded-xl md:rounded-2xl shadow-lg p-3 md:p-5 border border-slate-200/50 hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98] text-left">
                      <h4 className="font-bold text-slate-800 mb-1 md:mb-2 text-sm md:text-base">{product.name}</h4>
                      <p className="text-emerald-600 font-semibold text-base md:text-lg">{product.price.toLocaleString('hu-HU')} Ft</p>
                      {product.sku && <p className="text-slate-400 text-xs md:text-sm mt-1">{product.sku}</p>}
                    </button>
                  ))}
                </div>
              ) : (
                /* Terminal-style amount entry */
                <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6">
                  {/* Amount display */}
                  <div className="bg-slate-900 rounded-xl p-4 mb-4">
                    <p className="text-slate-400 text-sm mb-1">Összeg</p>
                    <p className="text-3xl md:text-4xl font-bold text-emerald-400 text-right font-mono">
                      {customAmountDisplay ? parseInt(customAmountDisplay).toLocaleString('hu-HU') : '0'} Ft
                    </p>
                  </div>
                  
                  {/* Numpad */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                      <button key={num} onClick={() => handleAmountDigit(String(num))} className="h-14 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 text-xl font-bold rounded-xl transition-all">
                        {num}
                      </button>
                    ))}
                    <button onClick={handleAmountClear} className="h-14 bg-red-100 hover:bg-red-200 text-red-600 font-bold rounded-xl transition-all">C</button>
                    <button onClick={() => handleAmountDigit("0")} className="h-14 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 text-xl font-bold rounded-xl transition-all">0</button>
                    <button onClick={handleAmountBackspace} className="h-14 bg-amber-100 hover:bg-amber-200 text-amber-600 font-bold rounded-xl transition-all">←</button>
                  </div>
                  
                  {/* Quick amounts */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {[100, 500, 1000, 5000].map(amt => (
                      <button key={amt} onClick={() => setCustomAmountDisplay(String(amt))} className="py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 text-sm font-semibold rounded-lg border border-slate-200">
                        {amt}
                      </button>
                    ))}
                  </div>
                  
                  <button data-testid="custom-amount-add" onClick={addCustomAmountToCart} disabled={!customAmountDisplay || parseInt(customAmountDisplay) <= 0} className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 text-white py-3 rounded-xl font-semibold transition-all">
                    Hozzáadás a kosárhoz
                  </button>
                </div>
              )}
            </div>

            {/* Right side - Cart (shows first on mobile) */}
            <div className="w-full lg:w-80 xl:w-96 order-1 lg:order-2">
              <div className="bg-white rounded-2xl shadow-xl p-4 md:p-6 border border-slate-200/50 lg:sticky lg:top-6">
                <h3 className="font-bold text-slate-800 text-lg mb-4 flex items-center gap-2">
                  <ShoppingCart size={20} />Kosár
                </h3>

                {cart.length === 0 ? (
                  <p className="text-slate-400 text-center py-8">A kosár üres</p>
                ) : (
                  <div className="space-y-3 mb-4 max-h-[400px] overflow-y-auto">
                    {cart.map(item => (
                      <div key={item.id} className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">{item.qty}× {item.name}</p>
                          <p className="text-sm text-slate-500">{(item.price * item.qty).toLocaleString('hu-HU')} Ft</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => updateQty(item.id, -1)} className="w-8 h-8 bg-slate-200 hover:bg-slate-300 rounded-lg font-bold text-slate-700">-</button>
                          <button onClick={() => updateQty(item.id, 1)} className="w-8 h-8 bg-slate-200 hover:bg-slate-300 rounded-lg font-bold text-slate-700">+</button>
                          <button onClick={() => removeFromCart(item.id)} className="w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg">
                            <X size={16} className="mx-auto" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Total */}
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-slate-600 font-medium">Végösszeg:</span>
                    <span data-testid="cart-total" className="text-2xl font-bold text-slate-800">{cartTotal.toLocaleString('hu-HU')} Ft</span>
                  </div>
                  <button data-testid="checkout-btn" onClick={() => setPaymentStep("payment")} disabled={cart.length === 0} className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:from-slate-300 disabled:to-slate-400 text-white font-semibold py-4 rounded-2xl transition-all">
                    Rendelés véglegesítése
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ==================== ORDERS VIEW ====================
  if (view === "orders") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <Header showLogout title="Rendelések" />
        <main className="max-w-4xl mx-auto px-4 md:px-6 py-4 md:py-6">
          <button onClick={() => setView("cashier")} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4">
            <ArrowLeft size={18} /><span>Vissza</span>
          </button>
          
          <div className="space-y-4">
            {orders.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <History size={48} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Még nincsenek rendelések</p>
              </div>
            ) : orders.map(order => (
              <div key={order.id} className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200/50">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-slate-500 text-sm">{new Date(order.created_at).toLocaleString('hu-HU')}</p>
                    <p className="text-xs text-slate-400">{order.payment_method === "cash" ? "Készpénz" : "Kártya"}</p>
                  </div>
                  <span className="text-xl font-bold text-emerald-600">{order.total.toLocaleString('hu-HU')} Ft</span>
                </div>
                <div className="space-y-1">
                  {order.items.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-slate-600">{item.qty}× {item.name}</span>
                      <span className="text-slate-800">{(item.price * item.qty).toLocaleString('hu-HU')} Ft</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // ==================== VOUCHER REDEEM VIEW ====================
  if (view === "voucher-redeem") {
    // Format voucher code with spaces for display (10 digits: 1234 5678 90)
    const formatVoucherCode = (code) => {
      if (!code) return '';
      const digits = code.replace(/[^0-9]/g, '');
      return `${digits.slice(0,4)} ${digits.slice(4,8)} ${digits.slice(8,10)}`.trim();
    };
    
    // Handle voucher code input - digits only
    const handleVoucherDigit = (digit) => {
      const clean = voucherCode.replace(/[^0-9]/g, '');
      if (clean.length < 10) {
        setVoucherCode(clean + digit);
      }
    };
    
    const handleVoucherClear = () => setVoucherCode("");
    const handleVoucherBackspace = () => {
      const clean = voucherCode.replace(/[^0-9]/g, '');
      setVoucherCode(clean.slice(0, -1));
    };
    
    const cleanCode = voucherCode.replace(/[^0-9]/g, '');
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <Header showLogout title="Szelvény beváltása" />
        <main className="max-w-2xl mx-auto px-6 py-6">
          <button onClick={() => { setView("cashier"); setFoundVoucher(null); setVoucherCode(""); }} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4">
            <ArrowLeft size={18} /><span>Vissza</span>
          </button>
          
          <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-200/50">
            {!foundVoucher ? (
              <>
                <h3 className="font-bold text-slate-800 text-lg mb-4 text-center">Szelvény azonosító</h3>
                
                {/* Code display boxes */}
                <div className="flex justify-center gap-1 mb-6">
                  {[0,1,2,3].map(i => (
                    <div key={i} className={`w-10 h-12 rounded-lg flex items-center justify-center text-xl font-mono font-bold border-2 ${cleanCode[i] ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-300'}`}>
                      {cleanCode[i] || '·'}
                    </div>
                  ))}
                  <span className="self-center mx-1 text-slate-300">-</span>
                  {[4,5,6,7].map(i => (
                    <div key={i} className={`w-10 h-12 rounded-lg flex items-center justify-center text-xl font-mono font-bold border-2 ${cleanCode[i] ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-300'}`}>
                      {cleanCode[i] || '·'}
                    </div>
                  ))}
                  <span className="self-center mx-1 text-slate-300">-</span>
                  {[8,9].map(i => (
                    <div key={i} className={`w-10 h-12 rounded-lg flex items-center justify-center text-xl font-mono font-bold border-2 ${cleanCode[i] ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-300'}`}>
                      {cleanCode[i] || '·'}
                    </div>
                  ))}
                </div>
                
                {/* Numeric keypad only (digits 0-9) */}
                <div className="grid grid-cols-3 gap-2 mb-4 max-w-xs mx-auto">
                  {['1','2','3','4','5','6','7','8','9'].map(d => (
                    <button key={d} onClick={() => handleVoucherDigit(d)} className="h-14 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xl font-bold rounded-xl">
                      {d}
                    </button>
                  ))}
                  <button onClick={handleVoucherClear} className="h-14 bg-red-100 hover:bg-red-200 text-red-600 font-bold rounded-xl">C</button>
                  <button onClick={() => handleVoucherDigit('0')} className="h-14 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xl font-bold rounded-xl">0</button>
                  <button onClick={handleVoucherBackspace} className="h-14 bg-amber-100 hover:bg-amber-200 text-amber-600 font-bold rounded-xl">←</button>
                </div>
                
                <button onClick={searchVoucher} disabled={cleanCode.length !== 10} className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2">
                  <Search size={20} />Keresés
                </button>
              </>
            ) : (
              <>
                {foundVoucher.is_winning ? (
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check size={40} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-emerald-600 mb-2">Nyerő szelvény!</h3>
                  </div>
                ) : (
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-slate-400 to-slate-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <AlertCircle size={40} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-600 mb-2">Nem nyerő szelvény</h3>
                  </div>
                )}

                <div className="bg-slate-50 rounded-xl p-4 mb-6 space-y-2">
                  <p><span className="text-slate-500">Azonosító:</span> <span className="font-mono font-bold text-slate-800">{formatVoucherCode(foundVoucher.code)}</span></p>
                  {foundVoucher.is_winning && <p><span className="text-slate-500">Nyeremény:</span> <span className="font-semibold text-emerald-600">{foundVoucher.prize_name}</span></p>}
                  <p><span className="text-slate-500">Kiállítás:</span> <span className="text-slate-800">{foundVoucher.issue_date}</span></p>
                </div>

                {foundVoucher.is_winning && (
                  <>
                    <h4 className="font-semibold text-slate-800 mb-3">Átvevő adatai</h4>
                    <div className="space-y-3 mb-6">
                      <input type="text" value={redeemForm.name} onChange={(e) => setRedeemForm({...redeemForm, name: e.target.value})} placeholder="Név" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                      <input type="tel" value={redeemForm.phone} onChange={(e) => setRedeemForm({...redeemForm, phone: e.target.value})} placeholder="Telefonszám" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                      <input type="email" value={redeemForm.email} onChange={(e) => setRedeemForm({...redeemForm, email: e.target.value})} placeholder="Email" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                      <input type="date" value={redeemForm.birth_date} onChange={(e) => setRedeemForm({...redeemForm, birth_date: e.target.value})} className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-700" />
                      <input type="date" value={redeemForm.birth_date} onChange={(e) => setRedeemForm({...redeemForm, birth_date: e.target.value})} className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                    </div>
                    <button onClick={redeemVoucher} className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold py-4 rounded-2xl">
                      Szelvény beváltása
                    </button>
                  </>
                )}

                <button onClick={() => { setFoundVoucher(null); setVoucherCode(""); }} className="w-full mt-4 bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold py-3 rounded-xl">
                  Másik szelvény
                </button>
              </>
            )}
          </div>
        </main>
      </div>
    );
  }

  // ==================== ADMIN VIEW ====================
  if (view === "admin") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <Header showLogout title="Adminisztráció" />
        <main className="max-w-7xl mx-auto px-6 py-6">
          {/* Tab buttons */}
          <div className="flex gap-3 mb-6 flex-wrap">
            {[
              { id: "kasszasok", label: "Kasszások", icon: Users },
              { id: "rendezvenyek", label: "Rendezvények", icon: Calendar },
              { id: "termekek", label: "Termékek", icon: Package },
              { id: "szelvenyek", label: "Szelvények", icon: Ticket },
              { id: "kijelzo", label: "Kosár kijelző", icon: Monitor },
            ].map((tab) => (
              <button key={tab.id} onClick={() => setActiveAdminTab(tab.id)} className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all ${activeAdminTab === tab.id ? "bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg" : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"}`}>
                <tab.icon size={18} />{tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-200/50">
            {/* KASSZÁSOK */}
            {activeAdminTab === "kasszasok" && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-slate-800">Kasszások</h2>
                  <button onClick={() => setShowCashierModal(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl font-semibold flex items-center gap-2">
                    <Plus size={18} />Új kasszás
                  </button>
                </div>
                <div className="space-y-3">
                  {cashiers.map(c => (
                    <div key={c.id} className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                          {c.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{c.name}</p>
                          <p className="text-sm text-slate-500">PIN: <span className="font-mono">{c.pin}</span> • {c.email}</p>
                        </div>
                      </div>
                      <button onClick={() => handleDeleteCashier(c.id)} className="text-red-500 hover:text-red-700 p-2">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* RENDEZVÉNYEK */}
            {activeAdminTab === "rendezvenyek" && (
              <div>
                {!selectedEvent ? (
                  <>
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-bold text-slate-800">Rendezvények</h2>
                      <button data-testid="add-event-btn" onClick={() => setShowEventModal(true)} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-xl font-semibold flex items-center gap-2">
                        <Plus size={18} />Új rendezvény
                      </button>
                    </div>
                    <div className="space-y-3">
                      {/* Sort events: active/upcoming first, then past events */}
                      {[...events]
                        .sort((a, b) => {
                          const today = new Date().toISOString().split('T')[0];
                          const aIsPast = a.date < today;
                          const bIsPast = b.date < today;
                          if (aIsPast !== bIsPast) return aIsPast ? 1 : -1; // Past events go to end
                          return b.date.localeCompare(a.date); // Newest first within each group
                        })
                        .map(e => {
                          const assignedCashiers = cashiers.filter(c => e.cashier_ids?.includes(c.id));
                          const today = new Date().toISOString().split('T')[0];
                          const isPastEvent = e.date < today;
                          return (
                            <div 
                              key={e.id} 
                              data-testid={`event-card-${e.id}`} 
                              onClick={() => setSelectedEvent(e)} 
                              className={`flex items-center justify-between rounded-xl p-4 cursor-pointer transition-colors ${
                                isPastEvent 
                                  ? 'bg-slate-100 opacity-50 hover:opacity-70' 
                                  : 'bg-slate-50 hover:bg-slate-100'
                              }`}
                            >
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className={`font-semibold ${isPastEvent ? 'text-slate-500' : 'text-slate-800'}`}>{e.name}</p>
                                  {isPastEvent && (
                                    <span className="text-xs px-2 py-0.5 bg-slate-300 text-slate-600 rounded-full">Lejárt</span>
                                  )}
                                </div>
                                <p className={`text-sm ${isPastEvent ? 'text-slate-400' : 'text-slate-500'}`}>{e.date} {e.start_time} • {e.location}</p>
                                <div className="flex gap-2 mt-1">
                                  <span className="text-xs text-slate-400">{e.product_ids?.length || 0} termék</span>
                                  {assignedCashiers.length > 0 && (
                                    <span className={`text-xs ${isPastEvent ? 'text-slate-400' : 'text-emerald-600'}`}>• {assignedCashiers.length} kasszás</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button onClick={(ev) => { ev.stopPropagation(); handleDeleteEvent(e.id); }} className="text-red-500 hover:text-red-700 p-2">
                                  <Trash2 size={18} />
                                </button>
                                <ArrowLeft size={20} className="text-slate-400 rotate-180" />
                              </div>
                            </div>
                          );
                        })}
                      {events.length === 0 && (
                        <p className="text-slate-500 text-center py-8">Még nincsenek rendezvények</p>
                      )}
                    </div>
                  </>
                ) : (
                  /* EVENT DETAILS WITH STATISTICS */
                  <div data-testid="event-stats-view">
                    <div className="flex items-center justify-between mb-4">
                      <button onClick={() => { setSelectedEvent(null); setEventStats(null); setEventTransactions([]); }} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm">
                        <ArrowLeft size={16} /><span>Vissza a rendezvényekhez</span>
                      </button>
                      <button data-testid="edit-event-btn" onClick={() => {
                        setEditingEvent(selectedEvent);
                        setEventForm({
                          name: selectedEvent.name,
                          date: selectedEvent.date,
                          start_time: selectedEvent.start_time || '',
                          location: selectedEvent.location || '',
                          product_ids: selectedEvent.product_ids || [],
                          cashier_ids: selectedEvent.cashier_ids || []
                        });
                      }} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-xl font-semibold flex items-center gap-2 text-sm">
                        <Settings size={16} />Esemény szerkesztése
                      </button>
                    </div>
                    
                    {/* Event Info Header */}
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-6 text-white mb-6">
                      <h2 className="text-2xl font-bold mb-2">{selectedEvent.name}</h2>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                        <div><span className="opacity-75">Dátum:</span><br /><span className="font-semibold">{selectedEvent.date}</span></div>
                        <div><span className="opacity-75">Kezdés:</span><br /><span className="font-semibold">{selectedEvent.start_time || '-'}</span></div>
                        <div><span className="opacity-75">Helyszín:</span><br /><span className="font-semibold">{selectedEvent.location || '-'}</span></div>
                        <div><span className="opacity-75">Termékek:</span><br /><span className="font-semibold">{selectedEvent.product_ids?.length || 0} db</span></div>
                        <div><span className="opacity-75">Kasszások:</span><br /><span className="font-semibold">{selectedEvent.cashier_ids?.length || 0} fő</span></div>
                      </div>
                      {selectedEvent.cashier_ids?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/20">
                          <span className="opacity-75 text-xs">Beosztott kasszások: </span>
                          <span className="text-sm">{cashiers.filter(c => selectedEvent.cashier_ids?.includes(c.id)).map(c => c.name).join(', ')}</span>
                        </div>
                      )}
                    </div>
                    
                    {eventStats ? (
                      <>
                        {/* Summary Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                          <div className="bg-emerald-50 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-emerald-600">{eventStats.summary.total_revenue.toLocaleString('hu-HU')} Ft</p>
                            <p className="text-xs text-emerald-700">Összes bevétel</p>
                          </div>
                          <div className="bg-blue-50 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-blue-600">{eventStats.summary.total_orders}</p>
                            <p className="text-xs text-blue-700">Rendelések</p>
                          </div>
                          <div className="bg-purple-50 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-purple-600">{eventStats.summary.avg_order_value.toLocaleString('hu-HU')} Ft</p>
                            <p className="text-xs text-purple-700">Átlag/rendelés</p>
                          </div>
                          <div className="bg-teal-50 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-teal-600">{eventStats.summary.cash_revenue.toLocaleString('hu-HU')} Ft</p>
                            <p className="text-xs text-teal-700">Készpénz</p>
                          </div>
                          <div className="bg-indigo-50 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-indigo-600">{eventStats.summary.card_revenue.toLocaleString('hu-HU')} Ft</p>
                            <p className="text-xs text-indigo-700">Bankkártya</p>
                          </div>
                        </div>

                        {/* Product Statistics */}
                        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
                          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Package size={18} />Termék statisztikák</h3>
                          {eventStats.product_stats.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr className="text-left text-xs text-slate-500 border-b">
                                    <th className="pb-2">Termék</th>
                                    <th className="pb-2 text-right">Darab</th>
                                    <th className="pb-2 text-right">Bevétel</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {eventStats.product_stats.map((ps, idx) => (
                                    <tr key={idx} className="border-b border-slate-100 last:border-0">
                                      <td className="py-2 font-medium text-slate-800">{ps.name}</td>
                                      <td className="py-2 text-right text-slate-600">{ps.quantity} db</td>
                                      <td className="py-2 text-right font-semibold text-emerald-600">{ps.revenue.toLocaleString('hu-HU')} Ft</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-slate-500 text-center py-4">Még nincs eladás</p>
                          )}
                        </div>

                        {/* Cashier Statistics */}
                        <div className="bg-white border border-slate-200 rounded-xl p-5">
                          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Users size={18} />Kasszás statisztikák</h3>
                          {eventStats.cashier_stats.length > 0 ? (
                            <div className="space-y-3">
                              {eventStats.cashier_stats.map((cs, idx) => (
                                <div key={idx} className="bg-slate-50 rounded-xl p-4">
                                  <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold">
                                      {cs.name?.charAt(0) || '?'}
                                    </div>
                                    <span className="font-semibold text-slate-800">{cs.name}</span>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                                    <div><span className="text-slate-500">Rendelések:</span><br /><span className="font-semibold text-slate-800">{cs.total_orders}</span></div>
                                    <div><span className="text-slate-500">Bevétel:</span><br /><span className="font-semibold text-emerald-600">{cs.total_revenue.toLocaleString('hu-HU')} Ft</span></div>
                                    <div><span className="text-slate-500">Készpénz:</span><br /><span className="font-semibold text-slate-800">{cs.cash_revenue.toLocaleString('hu-HU')} Ft</span></div>
                                    <div><span className="text-slate-500">Kártya:</span><br /><span className="font-semibold text-slate-800">{cs.card_revenue.toLocaleString('hu-HU')} Ft</span></div>
                                    <div><span className="text-slate-500">Termékek:</span><br /><span className="font-semibold text-slate-800">{cs.total_items} db</span></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-500 text-center py-4">Még nincs kasszás statisztika</p>
                          )}
                        </div>

                        {/* TRANSACTIONS SECTION */}
                        <div data-testid="transactions-section" className="bg-white border border-slate-200 rounded-xl p-5 mt-6">
                          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><History size={18} />Tranzakciók</h3>
                          {eventTransactions.length > 0 ? (
                            <div className="space-y-3 max-h-[500px] overflow-y-auto">
                              {eventTransactions.map((order) => (
                                <div key={order.id} data-testid={`transaction-row-${order.id}`} className="bg-slate-50 rounded-xl p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                      <div className="text-xs text-slate-500">
                                        {new Date(order.created_at).toLocaleDateString('hu-HU')} {new Date(order.created_at).toLocaleTimeString('hu-HU')}
                                      </div>
                                      <span className={`px-2 py-1 rounded text-xs font-semibold ${order.payment_method === 'cash' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                                        {order.payment_method === 'cash' ? 'Készpénz' : 'Kártya'}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="font-bold text-emerald-600">{order.total.toLocaleString('hu-HU')} Ft</span>
                                      <button 
                                        data-testid={`delete-transaction-${order.id}`}
                                        onClick={() => handleDeleteTransaction(order.id)} 
                                        className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg"
                                        title="Tranzakció törlése"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </div>
                                  
                                  <div className="text-sm mb-2">
                                    <span className="text-slate-500">Kasszás:</span> <span className="font-medium text-slate-800">{order.cashier_name}</span>
                                  </div>
                                  
                                  <div className="text-sm text-slate-600 mb-2">
                                    <span className="text-slate-500">Tételek:</span>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                      {order.items.map((item, idx) => (
                                        <span key={idx} className="bg-white px-2 py-1 rounded text-xs border border-slate-200">
                                          {item.qty}× {item.name} ({(item.price * item.qty).toLocaleString('hu-HU')} Ft)
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                  
                                  {order.payment_method === 'cash' && (
                                    <div className="text-xs text-slate-500 mt-2 flex gap-4">
                                      <span>Kapott: {order.paid_amount?.toLocaleString('hu-HU')} Ft</span>
                                      <span>Visszajáró: {order.change?.toLocaleString('hu-HU')} Ft</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-500 text-center py-4">Még nincsenek tranzakciók</p>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-slate-500">Statisztikák betöltése...</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* TERMÉKEK */}
            {activeAdminTab === "termekek" && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-slate-800">Termékek</h2>
                  <button onClick={() => setShowProductModal(true)} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl font-semibold flex items-center gap-2">
                    <Plus size={18} />Új termék
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {products.map(p => (
                    <div key={p.id} className="bg-slate-50 rounded-xl p-4 relative group">
                      <button onClick={() => handleDeleteProduct(p.id)} className="absolute top-2 right-2 text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 size={16} />
                      </button>
                      <p className="font-semibold text-slate-800">{p.name}</p>
                      <p className="text-emerald-600 font-bold">{p.price.toLocaleString('hu-HU')} Ft</p>
                      {p.sku && <p className="text-xs text-slate-400">{p.sku}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SZELVÉNYEK */}
            {activeAdminTab === "szelvenyek" && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-slate-800">Szelvények</h2>
                  <button onClick={() => setShowVoucherModal(true)} className="bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-xl font-semibold flex items-center gap-2">
                    <Plus size={18} />Új szelvény
                  </button>
                </div>
                <div className="space-y-2">
                  {vouchers.map(v => {
                    // Format code with spaces: 1234567890 -> 1234 5678 90
                    const formattedCode = `${v.code.slice(0,4)} ${v.code.slice(4,8)} ${v.code.slice(8)}`;
                    return (
                      <div key={v.id} className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-bold text-slate-800">{formattedCode}</span>
                          <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${v.is_winning ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                            {v.is_winning ? "Nyerő" : "Nem nyerő"}
                          </span>
                          {v.is_winning && v.prize_name && <span className="text-sm text-emerald-600">{v.prize_name}</span>}
                          {v.is_redeemed && <span className="px-2 py-1 rounded-lg text-xs font-semibold bg-blue-100 text-blue-700">Beváltva</span>}
                        </div>
                        <span className="text-sm text-slate-500">{v.issue_date}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* KOSÁR KIJELZŐ */}
            {activeAdminTab === "kijelzo" && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-slate-800">Kosár kijelző</h2>
                  <a href="/kasszakosar" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 text-sm flex items-center gap-1">
                    <Eye size={16} /> Megtekintés
                  </a>
                </div>
                
                {/* Enable/Disable Toggle */}
                <div className="bg-slate-50 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-800">Kassza kosár kijelző</h3>
                      <p className="text-sm text-slate-500">Ha be van kapcsolva, a /kasszakosar oldal elérhető</p>
                    </div>
                    <button 
                      onClick={() => updateDisplaySettings({...displaySettings, enabled: !displaySettings.enabled})}
                      className={`relative w-14 h-8 rounded-full transition-colors ${displaySettings.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                    >
                      <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${displaySettings.enabled ? 'translate-x-7' : 'translate-x-1'}`}></span>
                    </button>
                  </div>
                </div>

                {/* Ads Section */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Image size={18} /> Reklámok
                    </h3>
                    <button onClick={() => setShowAdModal(true)} className="bg-violet-500 hover:bg-violet-600 text-white px-4 py-2 rounded-xl font-semibold flex items-center gap-2 text-sm">
                      <Plus size={16} /> Új reklám
                    </button>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">A reklámképek a bal oldalon jelennek meg, ciklikusan váltakozva.</p>
                  
                  {displaySettings.ads?.length === 0 ? (
                    <div className="bg-slate-100 rounded-xl p-8 text-center">
                      <Image size={40} className="text-slate-400 mx-auto mb-2" />
                      <p className="text-slate-500">Még nincsenek reklámok</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {displaySettings.ads?.map((ad, idx) => (
                        <div key={idx} className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-3">
                          <img src={ad.url} alt="Reklám" className="w-20 h-14 object-cover rounded-lg" />
                          <div className="flex-1">
                            <p className="text-sm text-slate-600 truncate">{ad.url.substring(0, 50)}...</p>
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                              <Clock size={12} /> {ad.duration} másodperc
                            </p>
                          </div>
                          <button 
                            onClick={() => {
                              const newAds = displaySettings.ads.filter((_, i) => i !== idx);
                              updateDisplaySettings({...displaySettings, ads: newAds});
                            }}
                            className="text-red-500 hover:text-red-700 p-2"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>

        {/* MODALS */}
        {/* Cashier Modal */}
        {showCashierModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-800">Új kasszás</h3>
                <button onClick={() => setShowCashierModal(false)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                <input type="text" value={cashierForm.name} onChange={(e) => setCashierForm({...cashierForm, name: e.target.value})} placeholder="Név *" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                <input type="date" value={cashierForm.birth_date} onChange={(e) => setCashierForm({...cashierForm, birth_date: e.target.value})} className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                <input type="tel" value={cashierForm.phone} onChange={(e) => setCashierForm({...cashierForm, phone: e.target.value})} placeholder="Telefonszám" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                <input type="email" value={cashierForm.email} onChange={(e) => setCashierForm({...cashierForm, email: e.target.value})} placeholder="Email *" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                  💡 A kasszásokat a rendezvényeknél tudod hozzárendelni az adott naphoz.
                </p>
                <button onClick={handleCreateCashier} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 rounded-xl">
                  Létrehozás
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Event Modal */}
        {showEventModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-800">Új rendezvény</h3>
                <button onClick={() => setShowEventModal(false)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                <input type="text" value={eventForm.name} onChange={(e) => setEventForm({...eventForm, name: e.target.value})} placeholder="Esemény neve *" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                <input type="date" value={eventForm.date} onChange={(e) => setEventForm({...eventForm, date: e.target.value})} className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                <input type="time" value={eventForm.start_time} onChange={(e) => setEventForm({...eventForm, start_time: e.target.value})} className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                <input type="text" value={eventForm.location} onChange={(e) => setEventForm({...eventForm, location: e.target.value})} placeholder="Helyszín" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-2">Kasszások:</p>
                  <div className="space-y-2 max-h-32 overflow-y-auto bg-slate-50 rounded-xl p-3">
                    {cashiers.map(c => {
                      // Check if this cashier is already assigned to another event on the selected date
                      const conflictEvent = eventForm.date && events.find(ev => 
                        ev.date === eventForm.date && 
                        ev.cashier_ids?.includes(c.id)
                      );
                      const isConflict = !!conflictEvent;
                      
                      return (
                        <label key={c.id} className={`flex items-center gap-2 ${isConflict ? 'opacity-50' : 'cursor-pointer'}`}>
                          <input 
                            type="checkbox" 
                            checked={eventForm.cashier_ids.includes(c.id)} 
                            disabled={isConflict}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEventForm({...eventForm, cashier_ids: [...eventForm.cashier_ids, c.id]});
                              } else {
                                setEventForm({...eventForm, cashier_ids: eventForm.cashier_ids.filter(id => id !== c.id)});
                              }
                            }} 
                            className="w-5 h-5 rounded" 
                          />
                          <span className="text-sm">{c.name}</span>
                          {isConflict && (
                            <span className="text-xs text-red-500">({conflictEvent.name})</span>
                          )}
                        </label>
                      );
                    })}
                    {cashiers.length === 0 && <p className="text-xs text-slate-400">Még nincsenek kasszások</p>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">⚠️ Egy kasszás csak 1 rendezvényhez rendelhető egy adott napon</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-2">Termékek:</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {products.map(p => (
                      <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={eventForm.product_ids.includes(p.id)} onChange={(e) => {
                          if (e.target.checked) {
                            setEventForm({...eventForm, product_ids: [...eventForm.product_ids, p.id]});
                          } else {
                            setEventForm({...eventForm, product_ids: eventForm.product_ids.filter(id => id !== p.id)});
                          }
                        }} className="w-5 h-5 rounded" />
                        <span>{p.name} - {p.price} Ft</span>
                      </label>
                    ))}
                  </div>
                </div>
                <button onClick={handleCreateEvent} className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-xl">
                  Létrehozás
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Product Modal */}
        {showProductModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-800">Új termék</h3>
                <button onClick={() => setShowProductModal(false)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                <input type="text" value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} placeholder="Termék neve *" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                <input type="number" value={productForm.price} onChange={(e) => setProductForm({...productForm, price: parseInt(e.target.value) || 0})} placeholder="Ár (Ft)" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                <input type="text" value={productForm.sku} onChange={(e) => setProductForm({...productForm, sku: e.target.value})} placeholder="Cikkszám (opcionális)" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                <button onClick={handleCreateProduct} className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 rounded-xl">
                  Létrehozás
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Voucher Modal - simplified: no name, just winning/not winning + date */}
        {showVoucherModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-800">Új szelvény</h3>
                <button onClick={() => setShowVoucherModal(false)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                {/* Winning/Not winning toggle */}
                <div className="flex gap-2">
                  <button onClick={() => setVoucherForm({...voucherForm, is_winning: false})} className={`flex-1 py-3 rounded-xl font-semibold transition-all ${!voucherForm.is_winning ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    Nem nyerő
                  </button>
                  <button onClick={() => setVoucherForm({...voucherForm, is_winning: true})} className={`flex-1 py-3 rounded-xl font-semibold transition-all ${voucherForm.is_winning ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    Nyerő
                  </button>
                </div>
                
                {/* Prize name - only for winning */}
                {voucherForm.is_winning && (
                  <input type="text" value={voucherForm.prize_name} onChange={(e) => setVoucherForm({...voucherForm, prize_name: e.target.value})} placeholder="Nyeremény neve *" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                )}
                
                {/* Issue date */}
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Kiállítási dátum</label>
                  <input type="date" value={voucherForm.issue_date} onChange={(e) => setVoucherForm({...voucherForm, issue_date: e.target.value})} className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                </div>
                
                <button onClick={handleCreateVoucher} disabled={voucherForm.is_winning && !voucherForm.prize_name} className="w-full bg-rose-500 hover:bg-rose-600 disabled:bg-slate-300 text-white font-semibold py-3 rounded-xl">
                  Létrehozás
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Event Edit Modal */}
        {editingEvent && (
          <div data-testid="edit-event-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-800">Esemény szerkesztése</h3>
                <button onClick={() => { setEditingEvent(null); setEventForm({ name: "", date: "", start_time: "", location: "", product_ids: [], cashier_ids: [] }); }} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Esemény neve</label>
                  <input data-testid="edit-event-name" type="text" value={eventForm.name} onChange={(e) => setEventForm({...eventForm, name: e.target.value})} placeholder="Esemény neve *" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Dátum</label>
                  <input data-testid="edit-event-date" type="date" value={eventForm.date} onChange={(e) => setEventForm({...eventForm, date: e.target.value})} className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Kezdési idő</label>
                  <input data-testid="edit-event-time" type="time" value={eventForm.start_time} onChange={(e) => setEventForm({...eventForm, start_time: e.target.value})} className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Helyszín</label>
                  <input data-testid="edit-event-location" type="text" value={eventForm.location} onChange={(e) => setEventForm({...eventForm, location: e.target.value})} placeholder="Helyszín" className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-2">Kasszások:</p>
                  <div className="space-y-2 max-h-32 overflow-y-auto border border-slate-200 rounded-xl p-3 bg-slate-50">
                    {cashiers.map(c => {
                      // Check if this cashier is already assigned to another event on the selected date
                      // Exclude the current event being edited
                      const conflictEvent = eventForm.date && events.find(ev => 
                        ev.id !== editingEvent?.id &&
                        ev.date === eventForm.date && 
                        ev.cashier_ids?.includes(c.id)
                      );
                      const isConflict = !!conflictEvent;
                      const isCurrentlyAssigned = eventForm.cashier_ids?.includes(c.id) || false;
                      
                      return (
                        <label key={c.id} className={`flex items-center gap-2 p-1 rounded ${isConflict && !isCurrentlyAssigned ? 'opacity-50' : 'cursor-pointer hover:bg-white'}`}>
                          <input 
                            type="checkbox" 
                            checked={isCurrentlyAssigned} 
                            disabled={isConflict && !isCurrentlyAssigned}
                            onChange={(e) => {
                              const currentIds = eventForm.cashier_ids || [];
                              if (e.target.checked) {
                                setEventForm({...eventForm, cashier_ids: [...currentIds, c.id]});
                              } else {
                                setEventForm({...eventForm, cashier_ids: currentIds.filter(id => id !== c.id)});
                              }
                            }} 
                            className="w-5 h-5 rounded" 
                          />
                          <span className="text-slate-700 text-sm">{c.name}</span>
                          {isConflict && !isCurrentlyAssigned && (
                            <span className="text-xs text-red-500">({conflictEvent.name})</span>
                          )}
                        </label>
                      );
                    })}
                    {cashiers.length === 0 && <p className="text-xs text-slate-400">Még nincsenek kasszások</p>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">⚠️ Egy kasszás csak 1 rendezvényhez rendelhető egy adott napon</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-2">Termékek:</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-xl p-3">
                    {products.map(p => (
                      <label key={p.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                        <input data-testid={`edit-event-product-${p.id}`} type="checkbox" checked={eventForm.product_ids.includes(p.id)} onChange={(e) => {
                          if (e.target.checked) {
                            setEventForm({...eventForm, product_ids: [...eventForm.product_ids, p.id]});
                          } else {
                            setEventForm({...eventForm, product_ids: eventForm.product_ids.filter(id => id !== p.id)});
                          }
                        }} className="w-5 h-5 rounded" />
                        <span className="text-slate-700">{p.name} - {p.price.toLocaleString('hu-HU')} Ft</span>
                      </label>
                    ))}
                  </div>
                </div>
                <button data-testid="edit-event-save" onClick={handleUpdateEvent} disabled={!eventForm.name.trim() || !eventForm.date} className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl">
                  Mentés
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ad Upload Modal */}
        {showAdModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-800">Új reklám hozzáadása</h3>
                <button onClick={() => { setShowAdModal(false); setAdForm({ url: '', duration: 5 }); }} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Kép feltöltése</label>
                  <div className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center hover:border-violet-400 transition-colors">
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error("A fájl mérete maximum 5MB lehet!");
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = () => {
                            setAdForm({...adForm, url: reader.result});
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="hidden"
                      id="ad-file-input"
                    />
                    <label htmlFor="ad-file-input" className="cursor-pointer">
                      <Image size={32} className="mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-600">Kattints ide a kép kiválasztásához</p>
                      <p className="text-xs text-slate-400 mt-1">JPG, PNG, GIF - Max 5MB</p>
                    </label>
                  </div>
                </div>

                {/* OR divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-slate-200"></div>
                  <span className="text-xs text-slate-400">VAGY</span>
                  <div className="flex-1 border-t border-slate-200"></div>
                </div>

                {/* URL Input */}
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Kép URL</label>
                  <input 
                    type="url" 
                    value={adForm.url?.startsWith('data:') ? '' : adForm.url} 
                    onChange={(e) => setAdForm({...adForm, url: e.target.value})} 
                    placeholder="https://... (pl. külső kép URL)" 
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" 
                  />
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Megjelenítés ideje (másodperc)</label>
                  <input 
                    type="number" 
                    min="1" 
                    max="60" 
                    value={adForm.duration} 
                    onChange={(e) => setAdForm({...adForm, duration: parseInt(e.target.value) || 5})} 
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3" 
                  />
                </div>

                {/* Preview */}
                {adForm.url && (
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-slate-500">Előnézet:</p>
                      <button 
                        onClick={() => setAdForm({...adForm, url: ''})}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Törlés
                      </button>
                    </div>
                    <img src={adForm.url} alt="Előnézet" className="w-full h-40 object-contain rounded-lg bg-white" onError={(e) => e.target.style.display='none'} />
                  </div>
                )}

                <button 
                  onClick={() => {
                    if (!adForm.url) {
                      toast.error("Válassz ki egy képet vagy adj meg egy URL-t!");
                      return;
                    }
                    const newAds = [...(displaySettings.ads || []), { url: adForm.url, duration: adForm.duration }];
                    updateDisplaySettings({...displaySettings, ads: newAds});
                    setShowAdModal(false);
                    setAdForm({ url: '', duration: 5 });
                  }} 
                  disabled={!adForm.url}
                  className="w-full bg-violet-500 hover:bg-violet-600 disabled:bg-slate-300 text-white font-semibold py-3 rounded-xl"
                >
                  Hozzáadás
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
